import { alternateLinks } from "@/config/canonical";
import { usePageHeader } from "@/containers/MobileHeaderContext";
import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

interface Props {
  helmetTitle: string;
  helmetDescription?: string;
  helmetCanonicalLink?: string;
  backgroundImageUrl?: string;
  children?: React.ReactNode;
  back?: () => void;
  backgroundImageLayer?: string;
  shouldBeInTheDocumentFlow?: boolean; // if true, the header will be in the document flow
  /** Desktop only: at the header's right end, e.g. a guide link. */
  headerAction?: React.ReactNode;
  alternativeHeader?: React.ReactNode;
  alignCenter?: boolean;
  showOnTouchDeviceOnly?: boolean;
  /** Overrides helmetTitle for the browser <title> and og:title tags only */
  pageTitle?: string;
  /** Overrides helmetDescription for the <meta description> and og:description tags only */
  pageDescription?: string;
  /** Overrides helmetTitle for the visible <h1> element */
  h1?: string;
  /** Overrides helmetDescription for the visible subtitle paragraph */
  subtitle?: string;
  hidePageDescription?: boolean;
}

interface Header {
  topToPageClassName?: string;
  title?: string;
  description?: string;
  back?: () => void;
  headerAction?: React.ReactNode;
  shouldBeInTheDocumentFlow?: boolean;
  alternativeHeader?: React.ReactNode;
  h1?: string;
  subtitle?: string;
  hidePageDescription?: boolean;
}

// On mobile the header content is registered onto the persistent MobileTopNav
// (see usePageHeader in HeroSection); only the desktop overlay renders here.
const Header = ({
  topToPageClassName,
  title,
  description,
  back,
  shouldBeInTheDocumentFlow,
  h1,
  subtitle,
  hidePageDescription,
  headerAction,
}: Header) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  if (isTouchDevice) {
    return null;
  }
  return (
    <DesktopHeader
      topToPageClassName={topToPageClassName}
      title={title}
      description={description}
      back={back}
      shouldBeInTheDocumentFlow={shouldBeInTheDocumentFlow}
      h1={h1}
      subtitle={subtitle}
      hidePageDescription={hidePageDescription}
      headerAction={headerAction}
    />
  );
};

const DesktopHeader = ({
  topToPageClassName,
  title,
  description,
  back,
  shouldBeInTheDocumentFlow,
  h1,
  subtitle,
  hidePageDescription,
  headerAction,
}: Header) => {
  const visibleDescription = !hidePageDescription && (subtitle ?? description);
  return (
    <>
      {shouldBeInTheDocumentFlow && (
        <div className={visibleDescription ? "h-12" : "h-8"} />
      )}
      <div
        className={cn(
          "absolute z-11 top-0 left-0 items-center right-4 text-left justify-center w-full bg-black/50",
          topToPageClassName,
          !back && "px-8",
          visibleDescription ? "h-12" : "h-8",
        )}
      >
        <div className="flex flex-row gap-x-2 max-w-content mx-auto w-full">
          {back && (
            <button
              onClick={back}
              className={cn(
                "rotate-90 flex items-center justify-center",
                visibleDescription ? "size-12" : "w-12 h-8",
              )}
            >
              <div className="w-3 h-3 text-dark-tertiary">{arrow}</div>
            </button>
          )}
          <div className="mr-auto min-w-0">
            <div className="flex flex-row items-center gap-x-2">
              <h1 className="mt-0.5">{h1 ?? title}</h1>
            </div>
            {visibleDescription && (
              <h2 className="text-xs text-dark-tertiary truncate">
                {visibleDescription}
              </h2>
            )}
          </div>
          {headerAction && (
            <div className="flex shrink-0 items-center">{headerAction}</div>
          )}
        </div>
      </div>
    </>
  );
};

export const HeroSection = ({
  helmetTitle,
  helmetDescription,
  helmetCanonicalLink,
  children,
  back,
  backgroundImageUrl,
  backgroundImageLayer,
  shouldBeInTheDocumentFlow,
  alternativeHeader,
  alignCenter = true,
  showOnTouchDeviceOnly = false,
  pageTitle,
  pageDescription,
  h1,
  subtitle,
  hidePageDescription,
  headerAction,
}: Props) => {
  const { t } = useTranslation();
  const topToPage = useSelector(topToPageSelector);
  const isBigHeroSection = backgroundImageUrl || backgroundImageLayer;
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  // Feed this page's header into the persistent mobile top nav bar.
  usePageHeader({
    title: helmetTitle,
    description: helmetDescription,
    back,
    h1,
    subtitle,
    alternativeHeader,
  });

  if (showOnTouchDeviceOnly && !isTouchDevice) {
    return null;
  }
  return (
    <React.Fragment key={"hero-section-" + helmetTitle}>
      <Helmet>
        <title>{pageTitle ?? helmetTitle}</title>
        <meta
          name="description"
          content={pageDescription ?? helmetDescription}
        />
        <meta property="og:title" content={pageTitle ?? helmetTitle} />
        <meta
          property="og:description"
          content={pageDescription ?? helmetDescription}
        />
        {helmetCanonicalLink && (
          <link rel="canonical" href={helmetCanonicalLink} />
        )}
        {helmetCanonicalLink && alternateLinks(helmetCanonicalLink)}
      </Helmet>
      <Header
        topToPageClassName={topToPage.tailwindString}
        title={helmetTitle}
        description={helmetDescription}
        back={back}
        shouldBeInTheDocumentFlow={shouldBeInTheDocumentFlow}
        h1={h1}
        subtitle={subtitle}
        hidePageDescription={hidePageDescription}
        headerAction={headerAction}
      />

      {isBigHeroSection ? (
        <div
          className={cn(
            "min-h-38 z-10 bg-new-gray-900 items-end relative flex justify-center",
            shouldBeInTheDocumentFlow
              ? "touch-device:min-h-45"
              : "touch-device:min-h-57",
          )}
        >
          <img
            className={cn(
              "mx-auto h-38 object-center object-cover absolute top-0 left-1/2 -translate-x-1/2",
              shouldBeInTheDocumentFlow
                ? "touch-device:h-45"
                : "touch-device:h-57",
            )}
            src={backgroundImageUrl}
            alt={t("backgroundImageAlt", { title: helmetTitle })}
          />
          <div
            className="absolute top-0 left-0 h-38 touch-device:h-57 w-full"
            style={{
              background: backgroundImageLayer,
            }}
          />
          <div className={cn("z-10 w-full h-full", alignCenter && "my-auto")}>
            {children}
          </div>
        </div>
      ) : (
        children
      )}
    </React.Fragment>
  );
};
